import { CheckCircle2, Wrench } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ASSET_CONDITION_LABELS,
  formatPlainDate,
  REPAIR_REQUEST_STATUS_BLURBS,
  REPAIR_REQUEST_STATUS_LABELS,
  REPAIR_URGENCY_LABELS,
  tidyText,
} from '@cfl/assets-shared';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { DeviceArt } from '@/components/illustrations';
import { PageContainer } from '@/components/shell/page-container';
import { conditionVariant, PILL_CLASS } from '@/lib/badges';
import { ApiError } from '@/lib/api';
import { useAcknowledge, useMyAssets, type MyAssignment } from '@/lib/operations-api';
import { useCurrentUser } from '@/lib/session';
import { useMyRepairRequests } from '@/lib/repair-requests-api';
import { MyAssetRequests } from '@/routes/request-asset';
import { AttachmentStrip, ReportProblemDialog } from '@/routes/report-problem';
import { repairRequestVariant } from '@/lib/badges';

/**
 * Employee self-service.
 *
 * The only screen a plain employee can reach, and the whole reason acknowledgement
 * exists: the acknowledgement timestamp is a statement by this specific person that
 * they received this specific thing, which is what settles a question about a missing
 * laptop two years later. Nobody can record it on their behalf.
 */
export function MyAssetsPage() {
  const user = useCurrentUser();
  const { data: assignments, isPending } = useMyAssets();
  const [confirming, setConfirming] = React.useState<MyAssignment | null>(null);
  const [reporting, setReporting] = React.useState<MyAssignment | null>(null);
  const { data: requests } = useMyRepairRequests();

  /**
   * Which row a scanned sticker asked for. See AssetByCodePage: /a/AST-1007 resolves the
   * code against this same list and sends the holder here.
   *
   * The highlight is dropped as soon as it has been seen, so a refresh or a Back does
   * not re-announce a row the person is already looking at.
   */
  const [params, setParams] = useSearchParams();
  const scanned = params.get('asset');
  const scannedRef = React.useRef<HTMLLIElement | null>(null);
  React.useEffect(() => {
    if (!scanned || !scannedRef.current) return;
    scannedRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const timer = window.setTimeout(() => setParams({}, { replace: true }), 2500);
    return () => window.clearTimeout(timer);
  }, [scanned, setParams]);

  const open = assignments?.filter((a) => a.isOpen) ?? [];
  const past = assignments?.filter((a) => !a.isOpen) ?? [];
  const unconfirmed = open.filter((a) => a.acknowledgedAt === null);

  return (
    <PageContainer>
      {unconfirmed.length > 0 && (
        <Card className="border-warning/40 bg-warning/5">
          <CardContent className="py-4 sm:py-4">
            <p className="text-sm">
              <span className="font-medium">
                {unconfirmed.length} item{unconfirmed.length === 1 ? '' : 's'} to confirm.
              </span>{' '}
              Confirming records that you received it and the condition it arrived in. If
              something is missing or already damaged, say so in the note rather than
              confirming silently.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Issued to you</CardTitle>
          <CardDescription className="text-xs">
            {user.employeeCode} · everything the register currently says you are holding.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {isPending ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : open.length === 0 ? (
            <EmptyState art={DeviceArt} title="Nothing is issued to you" body="Hardware IT hands you shows up here. Need something? Ask for it below." />
          ) : (
            <ul className="divide-y">
              {open.map((assignment) => (
                <li
                  key={assignment.id}
                  ref={assignment.id === scanned ? scannedRef : undefined}
                  className={`flex flex-wrap items-center gap-3 py-3 ${
                    assignment.id === scanned
                      ? '-mx-3 rounded-md bg-primary/10 px-3 ring-1 ring-primary/40'
                      : ''
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{assignment.asset.name}</div>
                    <div className="at-num text-xs text-muted-foreground">
                      {assignment.asset.code}
                      {assignment.asset.serial ? ` · ${assignment.asset.serial}` : ''}
                      {` · issued ${formatPlainDate(assignment.assignedAt)}`}
                    </div>
                    {assignment.accessories.length > 0 && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        With it: {assignment.accessories.join(', ')}
                      </div>
                    )}
                    {assignment.remarks && (
                      <div className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
                        {tidyText(assignment.remarks)}
                      </div>
                    )}
                  </div>

                  <Badge variant={conditionVariant(assignment.condition)} className={PILL_CLASS}>
                    {ASSET_CONDITION_LABELS[assignment.condition]}
                  </Badge>

                  {assignment.acknowledgedAt ? (
                    <span className="flex items-center gap-1 text-xs text-success">
                      <CheckCircle2 className="size-3.5" />
                      confirmed
                    </span>
                  ) : (
                    <Button hint={"Confirm you received this asset"} size="sm" onClick={() => setConfirming(assignment)}>
                      Confirm receipt
                    </Button>
                  )}

                  {/* Available whether or not it has been confirmed - something can
                      break before anyone gets round to ticking the box. */}
                  <Button hint={"Tell IT something is wrong with this asset"} size="sm" variant="secondary" onClick={() => setReporting(assignment)}>
                    <Wrench className="mr-1.5 size-3.5" /> Report a problem
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {past.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Previously held</CardTitle>
            <CardDescription className="text-xs">
              Things you have had and given back.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y">
              {past.map((assignment) => (
                <li key={assignment.id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">{assignment.asset.name}</div>
                    <div className="at-num text-xs text-muted-foreground">
                      {assignment.asset.code} · {formatPlainDate(assignment.assignedAt)} →{' '}
                      {assignment.returnedAt ? formatPlainDate(assignment.returnedAt) : '—'}
                    </div>
                  </div>
                  {assignment.returnCondition && (
                    <Badge
                      variant={conditionVariant(assignment.returnCondition)}
                      className={PILL_CLASS}
                    >
                      returned {ASSET_CONDITION_LABELS[assignment.returnCondition].toLowerCase()}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* What I have reported, and what IT decided. Without this the report vanishes
          the moment it is sent, and the next thing the person does is email someone -
          which is exactly what this screen exists to replace. */}
      {requests && requests.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Problems you have reported</CardTitle>
            <CardDescription className="text-xs">
              What IT decided, and why.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y">
              {requests.map((request) => (
                <li key={request.id} className="space-y-2 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="at-num text-sm font-medium">{request.asset.code}</span>
                    <span className="text-sm text-muted-foreground">{request.asset.name}</span>
                    <Badge
                      variant={repairRequestVariant(request.status)}
                      className={`${PILL_CLASS} ml-auto`}
                    >
                      {REPAIR_REQUEST_STATUS_LABELS[request.status]}
                    </Badge>
                  </div>

                  <p className="whitespace-pre-wrap text-xs">{tidyText(request.description)}</p>

                  <p className="text-[11px] text-muted-foreground">
                    {REPAIR_URGENCY_LABELS[request.urgency]} ·{' '}
                    {REPAIR_REQUEST_STATUS_BLURBS[request.status]}
                  </p>

                  <AttachmentStrip attachments={request.attachments} />

                  {request.reviewNote && (
                    <div className="rounded-md bg-muted/60 p-2.5 text-xs">
                      <span className="font-medium">
                        {request.reviewedByName ?? 'IT'}:
                      </span>{' '}
                      {request.reviewNote}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Asking for hardware, and what happened to previous asks. Last on the page on
          purpose: what you are holding and what you owe a confirmation on come first,
          because those are the things this screen exists to make people act on. */}
      <MyAssetRequests />

      <AcknowledgeDialog assignment={confirming} onClose={() => setConfirming(null)} />
      <ReportProblemDialog assignment={reporting} onClose={() => setReporting(null)} />
    </PageContainer>
  );
}

function AcknowledgeDialog({
  assignment,
  onClose,
}: {
  assignment: MyAssignment | null;
  onClose: () => void;
}) {
  const acknowledge = useAcknowledge();
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (assignment) setNote('');
  }, [assignment]);

  const submit = async () => {
    if (!assignment) return;
    try {
      const result = await acknowledge.mutateAsync({ id: assignment.id, note: note.trim() || null });
      toast.success(
        result.alreadyAcknowledged
          ? 'This was already confirmed.'
          : `${assignment.asset.code} confirmed. Thank you.`,
      );
      onClose();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  return (
    <Dialog open={assignment !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm you received this</DialogTitle>
          <DialogDescription>
            {assignment?.asset.name}{' '}
            <span className="at-num">({assignment?.asset.code})</span>, issued to you on{' '}
            {assignment && formatPlainDate(assignment.assignedAt)}.
          </DialogDescription>
        </DialogHeader>

        {assignment && assignment.accessories.length > 0 && (
          <div className="rounded-md bg-muted/60 p-3 text-xs">
            <span className="font-medium">Handed over with it:</span>{' '}
            {assignment.accessories.join(', ')}
            <p className="mt-1 text-muted-foreground">
              Confirming covers these too. If something on this list is missing, say so
              below rather than confirming silently.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Anything to note? (optional)</Label>
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Scratch on the lid. No charger in the box."
          />
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Worth writing now rather than later — a note recorded on the day you received
            something carries far more weight than the same sentence six months on. It is
            added alongside IT’s handover note, not instead of it.
          </p>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button hint={"Confirm you received it"} onClick={() => void submit()} disabled={acknowledge.isPending}>
            {acknowledge.isPending ? 'Recording…' : 'Confirm receipt'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
